import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "@/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Info } from "lucide-react";
import { stepChanged, householdDataSaved, selectPseHelpAlreadyAssigned } from "@/app/booking-flow/publicBookingFlowSlice";
import type { Household } from "@/app/booking-flow/publicBookingFlowTypes";
import { useGetPublicBookingSeasonQuery } from "@/app/appointments/booking-season/bookingSeasonApi";
import { fundingSwitchNotices } from "@/app/booking-flow/fundingSwitchNotices";

// A switched-off funding program's message (AB#607), beside the option it concerns.
function SwitchNotice({ testId, message }: { testId: string; message: string | null }) {
  if (!message) return null;
  return (
    <div
      role="note"
      data-testid={testId}
      className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
    >
      <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
      {message}
    </div>
  );
}

export function GatherHouseholdDataStep() {
  const dispatch = useAppDispatch();

  const pseHelpAlreadyAssigned = useAppSelector(selectPseHelpAlreadyAssigned);
  const savedHouseholdData = useAppSelector(state => state.publicBookingFlow.householdData);
  // The page already holds the season; this reads the same cached answer.
  const notices = fundingSwitchNotices(useGetPublicBookingSeasonQuery().data?.programs);

  const [formData, setFormData] = useState<Household>(
    savedHouseholdData ?? {
      householdSize: -1,
      householdIncome: -1,
      address: "",
      energySource: "Other Heating and Energy Source",
      pseService: undefined,
    }
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? Number(value) : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(householdDataSaved(formData));
    dispatch(stepChanged(4));
  };

  const handleBack = () => {
    dispatch(stepChanged(2));
  };

  const isFormValid =
    formData.address.trim().length >= 5 &&
    formData.householdSize >= 1 &&
    formData.householdSize <= 24 &&
    formData.householdIncome >= 0 &&
    (formData.energySource !== "PSE" || !!formData.pseService);

  return (
    <Card className="w-full max-w-xl mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
      <CardHeader className="text-center px-4">
        <CardTitle className="text-2xl font-semibold text-slate-900">Household & Energy Details</CardTitle>
        <CardDescription className="text-slate-500">
          This information helps us determine the right assistance program for you.
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form id="step2-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="address">Home Address</Label>
            <Input required minLength={5} id="address" name="address" placeholder="123 Example St, Sample City, WA" value={formData.address} onChange={handleChange} />
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="householdSize">Household Size (1-24)</Label>
              <p id="household-size-note" className="text-sm text-slate-500">
                Enter the number of people living in your household including yourself.
              </p>
              <Input required id="householdSize" name="householdSize" type="number" min={1} max={24} value={formData.householdSize > -1 ? formData.householdSize : ""} onChange={handleChange} onFocus={(e) => e.target.select()} aria-describedby="household-size-note" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="householdIncome">Monthly Income ($)</Label>
              <p id="household-income-note" className="text-sm text-slate-500">
                Enter the total monthly income from everyone in your household combined.
              </p>
              <Input required id="householdIncome" name="householdIncome" type="number" min={0} value={formData.householdIncome > -1 ? formData.householdIncome : ""} onChange={handleChange} onFocus={(e) => e.target.select()} aria-describedby="household-income-note" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Heating and Energy Source</Label>
            <RadioGroup
              value={formData.energySource}
              onValueChange={(v) =>
                setFormData((p) => ({ ...p, energySource: v as any, pseService: v === "PSE" ? p.pseService : undefined }))
              }
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="PSE" id="energy-source-pse" />
                <Label htmlFor="energy-source-pse" className="font-normal">
                  Puget Sound Energy
                </Label>
              </div>
              <SwitchNotice testId="funding-switch-pse" message={notices.pse} />
              <div className="flex items-center gap-2">
                <RadioGroupItem value="Other Heating and Energy Source" id="energy-source-other" />
                <Label htmlFor="energy-source-other" className="font-normal">
                  Other Heating and Energy Source
                </Label>
              </div>
            </RadioGroup>
            <SwitchNotice testId="funding-switch-liheap" message={notices.liheap} />
            {/*
              AB#595: the energy source is a fact about the household, so it stays selectable whatever
              was received; only the PSE HELP program is withheld, server-side, on the guidelines step.
              NEEDS HUMAN DECISION (ams-hw9): wording.
            */}
            {pseHelpAlreadyAssigned && (
              <div
                role="note"
                data-testid="pse-help-already-assigned"
                className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700"
              >
                <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                PSE HELP has already been assigned to your household this program year; LIHEAP may still be available.
              </div>
            )}
          </div>

          {formData.energySource === "PSE" && (
            <div className="space-y-1.5">
              <Label>PSE Service</Label>
              <RadioGroup
                value={formData.pseService ?? ""}
                onValueChange={(v) => setFormData((p) => ({ ...p, pseService: v as any }))}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="Gas" id="pse-source-gas" />
                  <Label htmlFor="pse-source-gas" className="font-normal">Gas</Label>
                </div>
                <SwitchNotice testId="funding-switch-gas" message={notices.gas} />
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="Electric" id="pse-source-electric" />
                  <Label htmlFor="pse-source-electric" className="font-normal">Electric</Label>
                </div>
                <SwitchNotice testId="funding-switch-electric" message={notices.electric} />
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="Both gas and electric" id="pse-source-both" />
                  <Label htmlFor="pse-source-both" className="font-normal">Both gas and electric</Label>
                </div>
              </RadioGroup>
            </div>
          )}
        </form>

        <div className="flex flex-col sm:flex-row gap-3 mt-6 w-full max-w-md mx-auto">
          <Button type="button" variant="outline" className="flex flex-grow-1 text-base h-12" onClick={handleBack}>
            Back
          </Button>
          <Button
            form="step2-form"
            type="submit"
            className="flex flex-grow-2 text-base h-12"
            disabled={!isFormValid}
          >
            Continue
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
